import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { customReactSelectStyles } from "./medication-select";
import { ROUTE_OPTIONS } from "../../service-steps/clinical-assessment/data";

// Helper: given a beValue string from the API/DB, return the enriched ROUTE_OPTIONS entry
// or a plain fallback option so the select always has a value+beValue shape.
const resolveRouteOption = (beVal) => {
  if (!beVal) return null;
  const match = ROUTE_OPTIONS.find((o) => o.beValue === beVal || o.value === beVal);
  return match || { label: beVal, value: beVal, beValue: beVal };
};

const RouteSelect = ({
  onChange = () => {},
  selectedMedicine = {
    doseOptions: [],
    dose: null,
    formOptions: [],
    form: null,
    routeOptions: [],
    route: null,
  },
  isRequired = false,
  error = false,
  value: actualValue = { value: "", label: "" },
  isDisabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState(ROUTE_OPTIONS);
  const [medicineRouteOptions, setMedicineRouteOptions] = useState([]);
  const [showAllRoutes, setShowAllRoutes] = useState(false);
  const [value, setValue] = useState(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const { routeOptions, route } = selectedMedicine;
    if (routeOptions && routeOptions.length > 0) {
      const enriched = routeOptions.map((option) => {
        const match = ROUTE_OPTIONS.find((o) => o.beValue === option || o.label === option);
        return match || { label: option, value: option, beValue: option };
      });
      setMedicineRouteOptions(enriched);
      setShowAllRoutes(false);
      setOptions(enriched);
      if (route) {
        const resolved = enriched.find((o) => o.beValue === route || o.value === route) || resolveRouteOption(route);
        setValue(resolved);
        setShowError(false);
        onChange(resolved);
      } else {
        setShowError(true);
        setValue(null);
      }
    } else if (route) {
      const resolved = resolveRouteOption(route);
      setMedicineRouteOptions([]);
      setShowAllRoutes(false);
      setOptions([resolved]);
      setValue(resolved);
      setShowError(false);
      onChange(resolved);
    } else {
      setMedicineRouteOptions([]);
      setShowAllRoutes(false);
      setOptions(ROUTE_OPTIONS);
      setValue(null);
      setShowError(true);
    }
  }, [selectedMedicine]);

  // Sync displayed options when toggle changes
  useEffect(() => {
    if (medicineRouteOptions.length === 0) return;
    setOptions(showAllRoutes ? ROUTE_OPTIONS : medicineRouteOptions);
  }, [showAllRoutes, medicineRouteOptions]);

  const handleCreate = (inputValue) => {
    setIsLoading(true);
    setTimeout(() => {
      const newOption = {
        label: inputValue,
        value: inputValue,
        beValue: inputValue,
      };
      setIsLoading(false);
      setOptions((prevOptions) => [...prevOptions, newOption]);
      setValue(newOption);
      setShowError(isRequired && !newOption);
      onChange(newOption);
    }, 300);
  };

  const handleBlur = () => {
    if (isRequired && !value) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  };

  useEffect(() => {
    if (actualValue?.value) {
      // If already enriched (has beValue), use directly — preserves specific selections like Eye-Right
      // Only do a ROUTE_OPTIONS lookup for legacy plain DB values (no beValue)
      if (actualValue.beValue !== undefined) {
        setValue(actualValue);
      } else {
        const resolved = resolveRouteOption(actualValue.value);
        setValue(resolved);
      }
    } else {
      setValue(null);
    }
  }, [actualValue]);

  useEffect(() => {
    if (error) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  }, [error]);

  const hasMedicineRoutes = medicineRouteOptions.length > 0;

  const toggleOption = {
    label: showAllRoutes ? "Reset to Original" : "View More",
    value: "__TOGGLE__",
    isToggle: true,
  };

  const displayOptions = hasMedicineRoutes ? [toggleOption, ...options] : options;

  return (
    <div className="w-full col-span-4">
      <div
        className={`relative rounded-[4px] border-[1px] ${
          showError ? "border-red-200" : "border-gray-100"
        } ${!isDisabled && isRequired && value === null ? "bg-blue-100" : "bg-white"} ${isDisabled ? 'pointer-events-none opacity-70' : ''} px-3 py-3 focus-within:border-REELTeal focus-within:border-[2px]`}
      >
        <div className="">
          <div className="flex items-center justify-between mb-1">
            <span className="text-gray-900 text-body-xs">
              Route {isRequired && <span className="">*</span>}
            </span>
          </div>
          <CreatableSelect
            isClearable
            styles={customReactSelectStyles(isRequired, value)}
            isDisabled={isLoading || isDisabled}
            isLoading={isLoading}
            onChange={(newValue) => {
              if (newValue?.isToggle) {
                setShowAllRoutes((prev) => !prev);
                return;
              }
              setValue(newValue);
              setShowError(isRequired && !newValue);
              onChange(newValue);
            }}
            onCreateOption={handleCreate}
            onBlur={handleBlur}
            options={displayOptions}
            value={value}
            placeholder="Select route"
            className="col-span-4"
            formatOptionLabel={(option) => {
              if (option.isToggle) {
                return (
                  <span style={{ color: "#06657C", fontWeight: 500 }}>
                    {option.label}
                  </span>
                );
              }
              return option.label;
            }}
            isValidNewOption={(inputValue) =>
              !!inputValue && inputValue.trim() !== ""
            }
          />
        </div>
      </div>
      {showError && (
        <span className="block mt-1 text-red-200 text-body-xs">
          Route is required
        </span>
      )}
    </div>
  );
};

export default RouteSelect;
